import { useState } from 'react';

export type Edge = Partial<{
  source: string;
  sourceAnchor: number | any;
  target: string;
  targetAnchor: number;
  srcWorkflowNodeOutputIndex?: number | any;
}>;

const incrementIndex = (value: number) => (value ? value + 1 : undefined);

const decrementIndex = (value: number) => (value ? value - 1 : undefined);

// checks to see if either the sourceAnchor or outputIndex matches the index
const hasMatchedIndex = (index: number, sourceAnchor: number, outputIndex: number) =>
  sourceAnchor - 1 === index || outputIndex === index;

type EdgeRemapper = [
  edges: Edge[],
  actions: {
    shiftLastEdgeAt: (index: number) => void;
    removeEdgeAt: (index: number) => void;
    shiftEdgeAt: (index: number) => void;
    shiftEdgesAt: (index: number[]) => void;
    clearEdges: () => void;
    keepEdges: (index: number[]) => void;
  },
];

/*
  This hook is used to manage the remapping of edges on a split tile while it's beig actively edited.  We expose two functions to maniuplate the initial edges, removeEdgeAt
  and shiftLastEdgeAt.  The removeEdgeAt function removes an edge at the given index, if there's any edges greater than the index of the removed ege we shift those edges to the left.  
  The shiftLastEdgeAt function shifts the last edge at the given index to the next index, only if that edge exists.   We have to look at two edges to do this depending on the state the tile is in, if it
  has been saved the srcWorkflowNodeOutputIndex will be populated and it is 0 based.  If it hasn't been saved the sourceAnchor will be populated and it is 1 based, we always use the sourceAnchor first because thats
  what is used by g6 and our parsing function on save with make sure that the srcWorkflowNodeOutputIndex is always populated.
*/

export const useEdgeRemapper = (initialEdges: Edge[]): EdgeRemapper => {
  const [state, setState] = useState<Edge[]>(initialEdges);

  const removeEdgeAt = (index: number) => {
    const filteredEdges = [...state]?.filter((edge) => edge.sourceAnchor - 1 !== index);

    const nextState = filteredEdges?.map((edge: Edge) => {
      const { source, sourceAnchor, target, targetAnchor, srcWorkflowNodeOutputIndex } = edge;
      const pickedEdge = {
        source,
        sourceAnchor,
        target,
        targetAnchor,
        srcWorkflowNodeOutputIndex,
      };
      if (edge.sourceAnchor - 1 > index) {
        return {
          ...pickedEdge,
          sourceAnchor: decrementIndex(sourceAnchor),
          srcWorkflowNodeOutputIndex: decrementIndex(srcWorkflowNodeOutputIndex),
        };
      }
      return pickedEdge;
    });
    setState(nextState);
  };

  const shiftEdgesAt = (indexes: number[]) => {
    const nextState = [...state]?.map((edge: Edge) => {
      const { source, sourceAnchor, target, targetAnchor, srcWorkflowNodeOutputIndex } = edge;
      const pickedEdge = {
        source,
        sourceAnchor,
        target,
        targetAnchor,
        srcWorkflowNodeOutputIndex,
      };
      if (indexes.some((x) => edge.sourceAnchor - 1 === x)) {
        return {
          ...pickedEdge,
          sourceAnchor: incrementIndex(sourceAnchor),
          srcWorkflowNodeOutputIndex: incrementIndex(srcWorkflowNodeOutputIndex),
        };
      }
      return pickedEdge;
    });
    setState(nextState);
  };

  const shiftLastEdgeAt = (index: number) => {
    const nextState = [...state];
    const lastIndex = state.findIndex((edge: Edge) =>
      hasMatchedIndex(index, edge.sourceAnchor, edge.srcWorkflowNodeOutputIndex),
    );
    if (lastIndex > -1) {
      const edge = state[lastIndex];
      const { source, sourceAnchor, target, targetAnchor, srcWorkflowNodeOutputIndex } = edge;
      nextState[lastIndex] = {
        srcWorkflowNodeOutputIndex: incrementIndex(srcWorkflowNodeOutputIndex),
        source,
        sourceAnchor: incrementIndex(sourceAnchor),
        target,
        targetAnchor,
      };
      setState(nextState);
    }
  };

  const shiftEdgeAt = (index: number) => {
    const nextState = [...state];
    const lastIndex = state.findIndex((edge: Edge) =>
      hasMatchedIndex(index, edge.sourceAnchor, edge.srcWorkflowNodeOutputIndex),
    );
    if (lastIndex > -1) {
      const edge = state[lastIndex];
      const { source, sourceAnchor, target, targetAnchor, srcWorkflowNodeOutputIndex } = edge;
      nextState[lastIndex] = {
        srcWorkflowNodeOutputIndex: incrementIndex(srcWorkflowNodeOutputIndex),
        source,
        sourceAnchor: incrementIndex(sourceAnchor),
        target,
        targetAnchor,
      };
      setState(nextState);
    }
  };

  const clearEdges = () => {
    setState([]);
  };

  const keepEdges = (indexes: number[]) => {
    const nextState = [...state]?.filter((edge: Edge) => {
      const { sourceAnchor } = edge;
      return indexes.some((x) => sourceAnchor - 1 === x);
    });
    setState(nextState);
  };

  return [
    state,
    { removeEdgeAt, shiftLastEdgeAt, shiftEdgeAt, shiftEdgesAt, clearEdges, keepEdges },
  ];
};

export type AttributeSplitEdgeRemapper = [
  edges: Edge[],
  actions: {
    removeDefaultEdgeAt: (index: number) => void;
    shiftDefaultEdgeAt: (index: number) => void;
    removeEdgeAt: (index: number) => void;
  },
];

export const useAttributeSplitEdgeRemapper = (
  initialEdges: Edge[],
  hasDefaultEdge: boolean,
): AttributeSplitEdgeRemapper => {
  const [hasDefault, setHasDefault] = useState(hasDefaultEdge);
  const [state, { removeEdgeAt, shiftLastEdgeAt }] = useEdgeRemapper(initialEdges);
  const onAddEdge = (index: number) => {
    if (hasDefault) {
      shiftLastEdgeAt(index);
    }
  };
  const onRemoveDefault = (index: number) => {
    if (hasDefault) {
      removeEdgeAt(index);
      setHasDefault(false);
    }
  };
  return [
    state,
    {
      removeEdgeAt,
      shiftDefaultEdgeAt: onAddEdge,
      removeDefaultEdgeAt: onRemoveDefault,
    },
  ];
};

export const useABSplitEdgeRemapper = (initialEdges: Edge[]) => {
  const [state, { removeEdgeAt }] = useEdgeRemapper(initialEdges);
  return [state, { removeEdgeAt }];
};

export type HoldForReplyEdgeRemapper = [
  edges: Edge[],
  actions: {
    removeEdgeAt: (index: number) => void;
    shiftEdgesOtherAndNoReply: (otherReplyIndex: number, noReplyIndex: number) => void;
    addOtherReply: (index: number) => void;
    removeOtherReply: (index: number) => void;
    clearEdges: () => void;
  },
];

export const useHoldForReplyEdgeRemapper = (
  initialEdges: Edge[],
  hasOtherReply: boolean,
): HoldForReplyEdgeRemapper => {
  const [hasOther, setHasOther] = useState(hasOtherReply);
  const [state, { removeEdgeAt, shiftLastEdgeAt, shiftEdgesAt, clearEdges }] =
    useEdgeRemapper(initialEdges);

  const shiftEdgesOtherAndNoReply = (otherReplyIndex: number, noReplyIndex: number) => {
    if (hasOther) {
      shiftEdgesAt([noReplyIndex, otherReplyIndex]);
    } else {
      shiftEdgesAt([noReplyIndex]);
    }
  };

  const onRemoveOther = (index: number) => {
    removeEdgeAt(index);
    setHasOther(false);
  };

  const onAddOther = (currentNoReplyIndex: number) => {
    shiftLastEdgeAt(currentNoReplyIndex);
    setHasOther(true);
  };

  const handleRemoveEdgeAt = (index: number) => {
    removeEdgeAt(index);
  };

  return [
    state,
    {
      removeEdgeAt: handleRemoveEdgeAt,
      shiftEdgesOtherAndNoReply,
      removeOtherReply: onRemoveOther,
      addOtherReply: onAddOther,
      clearEdges,
    },
  ];
};
